import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  FileText, 
  Upload, 
  CheckCircle, 
  Clock, 
  CreditCard,
  Trash2, 
  Edit, 
  Eye,
  X,
  Building,
  Calendar,
  Euro,
  FileImage,
  Loader2,
  AlertCircle,
  Check,
  Download,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OCRFactures = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  const fetchInvoices = useCallback(async () => {
    try {
      let url = `${API_URL}/api/ocr?limit=50`;
      if (filter !== 'all') url += `&status=${filter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [token, filter]);

  const fetchBuildings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/buildings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBuildings(data.buildings || data || []);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ocr/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchInvoices(), fetchBuildings(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchInvoices, fetchBuildings, fetchStats]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert('Type de fichier non supporté. Utilisez JPEG, PNG, WebP ou PDF.');
        return;
      }
      // Validate file size (10 MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Fichier trop volumineux. Maximum: 10 Mo');
        return;
      }
      setUploadFile(file);
      setExtractedData(null);
    }
  };

  const handleExtract = async () => {
    if (!uploadFile) return;
    
    setExtracting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const res = await fetch(`${API_URL}/api/ocr/extract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        setExtractedData(data.extracted_data);
      } else {
        alert(data.error || 'Erreur lors de l\'extraction');
      }
    } catch (error) {
      console.error('Error extracting:', error);
      alert('Erreur lors de l\'extraction des données');
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!uploadFile || !extractedData || !selectedBuilding) {
      alert('Veuillez sélectionner un immeuble');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('building_id', selectedBuilding);
      formData.append('extracted_data', JSON.stringify(extractedData));
      formData.append('notes', notes);
      
      const res = await fetch(`${API_URL}/api/ocr/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        setShowUploadModal(false);
        resetUploadForm();
        fetchInvoices();
        fetchStats();
      } else {
        const error = await res.json();
        alert(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/ocr/${invoiceId}/validate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchInvoices();
        fetchStats();
      }
    } catch (error) {
      console.error('Error validating:', error);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/ocr/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchInvoices();
        fetchStats();
      }
    } catch (error) {
      console.error('Error marking paid:', error);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ocr/${invoiceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchInvoices();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setExtractedData(null);
    setSelectedBuilding('');
    setNotes('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      validated: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700'
    };
    const labels = {
      draft: 'Brouillon',
      validated: 'Validée',
      paid: 'Payée'
    };
    const icons = {
      draft: <Clock size={12} />,
      validated: <CheckCircle size={12} />,
      paid: <CreditCard size={12} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {icons[status]}
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="ocr-factures-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="text-[#00D3A8]" />
            OCR Factures
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Extraction automatique des données de factures par IA
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors"
          data-testid="upload-invoice-btn"
        >
          <Upload size={18} />
          Scanner une facture
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">Total factures</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">En attente</p>
            <p className="text-2xl font-bold text-amber-600">{stats.by_status?.draft?.count || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">Validées</p>
            <p className="text-2xl font-bold text-blue-600">{stats.by_status?.validated?.count || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">Montant total</p>
            <p className="text-2xl font-bold text-green-600">{formatMoney(stats.total_amount)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'Toutes' },
          { id: 'draft', label: 'Brouillons' },
          { id: 'validated', label: 'Validées' },
          { id: 'paid', label: 'Payées' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              filter === f.id
                ? 'bg-[#00D3A8] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Aucune facture</h3>
          <p className="text-gray-500">Scannez votre première facture pour commencer</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Facture</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Fournisseur</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Immeuble</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Montant</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-800">
                        {invoice.extracted_data?.numero_facture || 'N° inconnu'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(invoice.extracted_data?.date_facture)}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-gray-800">{invoice.extracted_data?.fournisseur_nom || '-'}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Building size={14} />
                      {invoice.building_name || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-gray-800">
                      {formatMoney(invoice.extracted_data?.montant_ttc)}
                    </p>
                    {invoice.extracted_data?.montant_ht && (
                      <p className="text-xs text-gray-500">
                        HT: {formatMoney(invoice.extracted_data?.montant_ht)}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(invoice.status)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowDetailModal(true);
                        }}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        title="Voir détails"
                      >
                        <Eye size={16} />
                      </button>
                      {invoice.status === 'draft' && (
                        <button
                          onClick={() => handleValidate(invoice.id)}
                          className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                          title="Valider"
                        >
                          <Check size={16} />
                        </button>
                      )}
                      {invoice.status === 'validated' && (
                        <button
                          onClick={() => handleMarkPaid(invoice.id)}
                          className="p-2 text-green-500 hover:bg-green-50 rounded-lg"
                          title="Marquer payée"
                        >
                          <CreditCard size={16} />
                        </button>
                      )}
                      {invoice.file_url && (
                        <a
                          href={`${API_URL}${invoice.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="Télécharger"
                        >
                          <Download size={16} />
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Sparkles className="text-[#00D3A8]" />
                Scanner une facture
              </h2>
              <button onClick={() => { setShowUploadModal(false); resetUploadForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* File Upload Zone */}
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  uploadFile ? 'border-[#00D3A8] bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="invoice-file"
                />
                
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-4">
                    <FileImage className="w-12 h-12 text-[#00D3A8]" />
                    <div className="text-left">
                      <p className="font-medium text-gray-800">{uploadFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(uploadFile.size / 1024 / 1024).toFixed(2)} Mo
                      </p>
                    </div>
                    <button
                      onClick={() => { setUploadFile(null); setExtractedData(null); }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <label htmlFor="invoice-file" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">
                      Glissez une facture ici ou cliquez pour sélectionner
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      JPEG, PNG, WebP ou PDF (max 10 Mo)
                    </p>
                  </label>
                )}
              </div>

              {/* Extract Button */}
              {uploadFile && !extractedData && (
                <button
                  onClick={handleExtract}
                  disabled={extracting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Extraction en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      Extraire les données avec l'IA
                    </>
                  )}
                </button>
              )}

              {/* Extracted Data Preview */}
              {extractedData && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle size={20} />
                    <span className="font-medium">Données extraites avec succès</span>
                    {extractedData.confidence_score && (
                      <span className="text-sm text-gray-500">
                        (Confiance: {Math.round(extractedData.confidence_score * 100)}%)
                      </span>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-500 mb-3">INFORMATIONS FACTURE</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Numéro</span>
                          <span className="font-medium">{extractedData.numero_facture || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Date</span>
                          <span className="font-medium">{extractedData.date_facture || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Échéance</span>
                          <span className="font-medium">{extractedData.date_echeance || '-'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-500 mb-3">FOURNISSEUR</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Nom</span>
                          <span className="font-medium">{extractedData.fournisseur_nom || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">SIRET</span>
                          <span className="font-medium">{extractedData.fournisseur_siret || '-'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                      <h4 className="text-sm font-semibold text-gray-500 mb-3">MONTANTS</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-500">HT</p>
                          <p className="text-lg font-bold text-gray-800">
                            {formatMoney(extractedData.montant_ht)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">TVA ({extractedData.taux_tva || 20}%)</p>
                          <p className="text-lg font-bold text-gray-800">
                            {formatMoney(extractedData.montant_tva)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">TTC</p>
                          <p className="text-lg font-bold text-[#00D3A8]">
                            {formatMoney(extractedData.montant_ttc)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Building Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Immeuble concerné *</label>
                    <select
                      value={selectedBuilding}
                      onChange={(e) => setSelectedBuilding(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    >
                      <option value="">Sélectionner un immeuble</option>
                      {buildings.map((b) => (
                        <option key={b.id} value={b.id}>{b.nom || b.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optionnel)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                      rows={2}
                      placeholder="Ajouter des notes..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {extractedData && (
              <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={!selectedBuilding || uploading}
                  className="flex items-center gap-2 px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Enregistrer la facture
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Facture {selectedInvoice.extracted_data?.numero_facture}
                </h2>
                <p className="text-sm text-gray-500">{selectedInvoice.extracted_data?.fournisseur_nom}</p>
              </div>
              <button
                onClick={() => { setShowDetailModal(false); setSelectedInvoice(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedInvoice.status)}
                <span className="text-sm text-gray-500">
                  Créée le {formatDate(selectedInvoice.created_at)}
                </span>
              </div>

              {/* Info Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800 border-b pb-2">Informations facture</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Numéro</span>
                      <span className="font-medium">{selectedInvoice.extracted_data?.numero_facture || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date facture</span>
                      <span className="font-medium">{selectedInvoice.extracted_data?.date_facture || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date échéance</span>
                      <span className="font-medium">{selectedInvoice.extracted_data?.date_echeance || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Immeuble</span>
                      <span className="font-medium">{selectedInvoice.building_name || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800 border-b pb-2">Fournisseur</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Nom</span>
                      <span className="font-medium">{selectedInvoice.extracted_data?.fournisseur_nom || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">SIRET</span>
                      <span className="font-medium">{selectedInvoice.extracted_data?.fournisseur_siret || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Adresse</span>
                      <span className="font-medium text-right max-w-[200px]">
                        {selectedInvoice.extracted_data?.fournisseur_adresse || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amounts */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-4">Montants</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-500">Montant HT</p>
                    <p className="text-xl font-bold text-gray-800">
                      {formatMoney(selectedInvoice.extracted_data?.montant_ht)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      TVA ({selectedInvoice.extracted_data?.taux_tva || 20}%)
                    </p>
                    <p className="text-xl font-bold text-gray-800">
                      {formatMoney(selectedInvoice.extracted_data?.montant_tva)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Montant TTC</p>
                    <p className="text-xl font-bold text-[#00D3A8]">
                      {formatMoney(selectedInvoice.extracted_data?.montant_ttc)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedInvoice.extracted_data?.description && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                  <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                    {selectedInvoice.extracted_data.description}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Notes</h4>
                  <p className="text-gray-600 text-sm bg-yellow-50 p-3 rounded-lg">
                    {selectedInvoice.notes}
                  </p>
                </div>
              )}

              {/* File Link */}
              {selectedInvoice.file_url && (
                <a
                  href={`${API_URL}${selectedInvoice.file_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Download size={18} />
                  Télécharger le fichier original
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCRFactures;
